import { AsyncPipe } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    DOCUMENT,
    effect,
    inject,
    signal,
    type WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import moment from 'moment';
import { fromEvent, interval, map, type Observable, Subject, takeUntil, timer } from 'rxjs';
import { filter, throttle } from 'rxjs/operators';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { ClipboardService } from '@app/shared/services/clipboard.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';

@Component({
    selector: 'cor-footer',
    templateUrl: './footer.component.html',
    imports: [AsyncPipe, TooltipModule],
})
export class FooterComponent {
    protected version = {
        id: signal(''),
        commitId: signal(''),
        buildDate: signal(''),
    };

    countDown$: Observable<string>;

    isCountDownStopped$: Subject<boolean> = new Subject();

    readonly manualRefreshReminderTimeout = computed(
        () => this.appConfigService.appConfig().manualRefreshReminderTimeout || 0,
    );

    protected readonly isCountDownRecalculating: WritableSignal<boolean> = signal(false);

    protected readonly authService = inject(AuthenticationService);
    protected readonly destroyRef = inject(DestroyRef);
    private readonly clipboardService = inject(ClipboardService);
    private readonly toast = inject(ToastNotificationService);
    private readonly appConfigService = inject(AppConfigService);
    private readonly document = inject(DOCUMENT);

    constructor() {
        const config = this.appConfigService.appConfig();
        if (config.versions?.['corridor-app']) {
            // Possible formats:
            // v1.13.1.dev23100+gb3887d3fd6.d20250716
            // v1.13.1.dev23100+gb3887d3fd6
            const versionRegex = /^([^+]+)\+g([a-f0-9]+)(?:\.d(\d{4})(\d{2})(\d{2}))?$/;
            const match = config.versions['corridor-app'].match(versionRegex);

            if (match) {
                const [, id, commitId, year, month, day] = match;
                this.version.id.set(id); // e.g. 1.13.1.dev23100
                this.version.commitId.set(commitId); // e.g. b3887d3fd6

                if (year && month && day) {
                    this.version.buildDate.set(`${day}-${month}-${year}`); // e.g. 16-07-2025
                }
            }
        }

        effect(() => {
            if (this.manualRefreshReminderTimeout() > 0) {
                this.authService.tokenChange$
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(_ => this.initCounter());

                /**
                 * This helps logout timer to be in sync when
                 * 1. user returns to the tab after being in some other tab
                 * 2. browser maximized after it was minimized
                 *
                 * throttle is added so that function isn't invoked if the user returns
                 * to the tab within 1 second. This is added for performance.
                 */
                fromEvent(this.document, 'visibilitychange')
                    .pipe(
                        takeUntilDestroyed(this.destroyRef),
                        filter(() => this.document.visibilityState === 'visible'),
                        throttle(_ => interval(1000)),
                    )
                    .subscribe(_ => this.initCounter());
            }
        });
    }

    protected refreshToken() {
        this.isCountDownRecalculating.set(true);
        this.authService
            .refreshToken()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: response => {
                    this.authService.saveTokens(response);
                },
                error: () => {
                    this.toast.showError('Failed to continue session.');
                },
            })
            .add(() => {
                this.isCountDownRecalculating.set(false);
                this.isCountDownStopped$.next(false);
            });
    }

    initCounter() {
        if (!this.authService.getToken()) return;

        const tokenTimeoutTimer = this.authService.getSecondsToExpiry();

        this.countDown$ = timer(0, 1000)
            .pipe(takeUntil(this.isCountDownStopped$))
            .pipe(
                map(sec => {
                    const seconds = tokenTimeoutTimer - sec;

                    if (seconds <= 0) {
                        this.isCountDownStopped$.next(true);
                    }

                    return this.formattedTime(tokenTimeoutTimer, seconds);
                }),
            );
    }

    protected copyCommitId() {
        this.clipboardService.copyToClipboard(this.version.commitId()).then(
            () => console.log('Commit ID copied to clipboard.'),
            () => console.error('Failed to copy commit ID.'),
        );
    }

    /**
     * returns time in hh:mm:ss format if tokenTimeoutTimer >= 1hr; else returns time in mm:ss
     */
    formattedTime(tokenTimeoutTimer: number, seconds: number) {
        if (tokenTimeoutTimer < 3600) {
            return moment.utc(seconds * 1000).format('mm:ss');
        }

        // when hour is 0 then 12 is returned from moment
        if (seconds < 3600) {
            return `00:${moment.utc(seconds * 1000).format('mm:ss')}`;
        }

        return moment.utc(seconds * 1000).format('hh:mm:ss');
    }
}
